import {
  BookedVisitStatusType,
  FreeTextCriteria,
  Maybe,
  OrgCriteria,
  PatientAppStatusType,
  PatientFinalContactStatusType,
  PatientMedicationStatusType,
  PatientPauseStatusType,
  PatientStatusType,
  PatientStudyStatusType,
  PatientVitalStatusType,
  UserId,
} from '@csp/csp-common-model';
import { BookedVisit } from '@csp/csp-common-visits';
import {
  CriteriaOperatorType,
  CriteriaType,
  CriterionV1,
  QueryV1,
  RoleType,
  SortOrderType,
} from '@csp/dmdp-api-common-dto';
import {
  DmdpUserStatusType,
  HealthEventSortOrderAttrType,
  PATIENT_VISIT_V1_META_TYPE,
  UserCriteriaFieldType,
  UserIncludeFieldType,
  UserSortFieldType,
} from '@csp/dmdp-api-user-dto';
import { PatientVisitUtil } from '@csp/csp-common-patient-mapper';
import { EnumUtil } from '@csp/csp-common-enum-util';
import { isDefined } from '@csp/csp-common-util';
import { ZonedDateTimeFactory, ZonedDateTimeFormatter, zonedSubtractDuration } from '@csp/csp-common-date-util';
import { PatientPauseMetaMapper } from '../../meta/mapper/PatientPauseMetaMapper';
import { CloseoutVisitCriteria } from './CloseoutVisitCriteria';
import { ComplianceCriteria } from './ComplianceCriteria';
import { PagedPatientQueryIn } from './PagedPatientQueryIn';
import { PatientStatusCriteria } from './PatientStatusCriteria';
import { PatientCriteria } from './PatientCriteria';

export type PatientQuery = {
  queryV1: QueryV1;
};

const DEFAULT_FIELDS = [
  UserIncludeFieldType.USER_ID,
  UserIncludeFieldType.AUDIT,
  UserIncludeFieldType.STUDY_ECODE,
  UserIncludeFieldType.META,
  UserIncludeFieldType.CUSTOM_STATUSES,
  UserIncludeFieldType.PREVIOUS_CUSTOM_STATUSES,
  UserIncludeFieldType.OCCASION,
  UserIncludeFieldType.LOCALIZATION,
  UserIncludeFieldType.ROLE_ACCESS_REF,
  UserIncludeFieldType.STATUS,
  UserIncludeFieldType.STUDY_RANDOMIZED_TIMESTAMP,
  UserIncludeFieldType.STUDY_SCREENED_TIMESTAMP,
  UserIncludeFieldType.STUDY_END_TIMESTAMP,
  UserIncludeFieldType.IDP,
  UserIncludeFieldType.LAST_CLIENT_ACTIVITY,
];

const DEFAULT_SORTING = [
  {
    field: UserSortFieldType.META_VALUE,
    subField: HealthEventSortOrderAttrType.HEALTH_EVENT_SORT_ORDER,
    order: SortOrderType.DESC,
  },
  {
    field: UserSortFieldType.USER_ID,
    order: SortOrderType.DESC,
  },
];

const toStatusCriteria = (
  type: PatientStatusType,
  statuses: Maybe<
    | PatientStudyStatusType[]
    | PatientMedicationStatusType[]
    | PatientAppStatusType[]
    | PatientFinalContactStatusType[]
    | PatientVitalStatusType[]
  >,
): Maybe<CriterionV1> => {
  if (statuses?.length) {
    return {
      operator: CriteriaOperatorType.AND,
      criteria: [
        {
          fieldCriterion: {
            field: UserCriteriaFieldType.CUSTOM_STATUS_TYPE,
            type: CriteriaType.EQ,
            values: [type],
          },
        },
        {
          fieldCriterion: {
            field: UserCriteriaFieldType.CUSTOM_STATUS_VALUE,
            type: CriteriaType.IN,
            values: statuses,
          },
        },
      ],
    };
  } else {
    return undefined;
  }
};

const toLastClientActivityStatusCriteria = (inactivityThresholdInDays: Maybe<number>): Maybe<CriterionV1> => {
  if (inactivityThresholdInDays) {
    return {
      fieldCriterion: {
        field: UserCriteriaFieldType.ACTIVE_AT_LATEST,
        type: CriteriaType.LESS_THAN_EQ,
        values: [
          ZonedDateTimeFormatter.toZonedDateTimeString(
            zonedSubtractDuration(ZonedDateTimeFactory.now(), { days: inactivityThresholdInDays }),
          ),
        ],
      },
    };
  } else {
    return undefined;
  }
};

const finalMedicationStatuses: PatientMedicationStatusType[] = [
  PatientMedicationStatusType.TREATMENT_DISCONTINUED,
  PatientMedicationStatusType.FOLLOW_UP,
  PatientMedicationStatusType.SURVIVAL_FOLLOW_UP,
  PatientMedicationStatusType.TREATMENT_COMPLETED,
];

const calculateStatuses = (statuses: Maybe<PatientMedicationStatusType[]>): PatientMedicationStatusType[] => [
  ...new Set(
    statuses?.flatMap(status => {
      if (
        status === PatientMedicationStatusType.TREATMENT_COMPLETED ||
        status === PatientMedicationStatusType.TREATMENT_DISCONTINUED
      ) {
        return finalMedicationStatuses;
      } else {
        return [status];
      }
    }),
  ),
];

const toFreeTextCriterionV1 = (freeText?: FreeTextCriteria): Maybe<CriterionV1> => {
  const trimmedText = freeText?.text ? String(freeText.text).trim() : undefined;
  const text = trimmedText?.length ? trimmedText : undefined;
  return text
    ? {
        fieldCriterion: {
          field: UserCriteriaFieldType.STUDY_ECODE,
          type: CriteriaType.LIKE,
          values: [text.toUpperCase()],
        },
      }
    : undefined;
};

const toUserIdsCriterionV1 = (userIds?: UserId[]): Maybe<CriterionV1> => {
  const ids = userIds && userIds.length ? userIds : undefined;
  return ids
    ? {
        fieldCriterion: {
          field: UserCriteriaFieldType.USER_ID,
          type: CriteriaType.IN,
          values: ids,
        },
      }
    : undefined;
};

const toUserStatusCriterionV1 = (userStatuses?: DmdpUserStatusType[]): Maybe<CriterionV1> =>
  userStatuses && userStatuses.length
    ? {
        fieldCriterion: {
          field: UserCriteriaFieldType.STATUS,
          type: CriteriaType.IN,
          values: userStatuses,
        },
      }
    : undefined;

const toPatientPauseIntervalCriteria = (nowMillis: number, patientPauseStatusType: string): CriterionV1 => ({
  operator: CriteriaOperatorType.AND,
  criteria: [
    {
      fieldCriterion: {
        field: UserCriteriaFieldType.META_VALUE,
        subField: PatientPauseMetaMapper.toPauseUtcPath(patientPauseStatusType),
        type: CriteriaType.LESS_THAN,
        values: [nowMillis],
      },
    },
    {
      fieldCriterion: {
        field: UserCriteriaFieldType.META_VALUE,
        subField: PatientPauseMetaMapper.toResumeUtcPath(patientPauseStatusType),
        type: CriteriaType.GREATER_THAN,
        values: [nowMillis],
      },
    },
  ],
});

const toPausedTasksCriterionV1 = (pausedTasks?: boolean): Maybe<CriterionV1> => {
  const nowMillis = Date.now();

  return pausedTasks
    ? {
        operator: CriteriaOperatorType.OR,
        criteria: [
          {
            operator: CriteriaOperatorType.AND,
            criteria: [
              {
                fieldCriterion: {
                  field: UserCriteriaFieldType.META_TYPE,
                  type: CriteriaType.EQ,
                  values: ['PATIENT_PAUSE_V1'],
                },
              },
              {
                operator: CriteriaOperatorType.OR,
                criteria: EnumUtil.asStrings(PatientPauseStatusType).map(type =>
                  toPatientPauseIntervalCriteria(nowMillis, type),
                ),
              },
            ],
          },
          {
            operator: CriteriaOperatorType.AND,
            criteria: [
              {
                fieldCriterion: {
                  field: UserCriteriaFieldType.META_TYPE,
                  type: CriteriaType.EQ,
                  values: ['PATIENT_MEDICATION_V1'],
                },
              },
              {
                fieldCriterion: {
                  field: UserCriteriaFieldType.META_VALUE,
                  subField: 'pausedRequestCodes',
                  type: CriteriaType.EXISTS,
                  values: [true],
                },
              },
            ],
          },
        ],
      }
    : undefined;
};

const toOrgCriterionV1 = (org?: OrgCriteria): Maybe<CriterionV1> =>
  org?.siteIds?.length
    ? {
        fieldCriterion: {
          field: UserCriteriaFieldType.ACCESS_REF,
          type: CriteriaType.IN,
          values: org.siteIds,
        },
      }
    : undefined;

const createCloseoutValueCriterion = (type: CriteriaType, values: unknown[], subAttr?: string): CriterionV1 => ({
  fieldCriterion: {
    field: UserCriteriaFieldType.META_VALUE,
    subField: subAttr ? `closeout.${subAttr}` : 'closeout',
    type,
    values,
  },
});

const visitMetaCriterion: CriterionV1 = {
  fieldCriterion: {
    field: UserCriteriaFieldType.META_TYPE,
    type: CriteriaType.EQ,
    values: [PATIENT_VISIT_V1_META_TYPE],
  },
};

const noFinalContactCriterion: CriterionV1 = {
  operator: CriteriaOperatorType.OR,
  criteria: [
    {
      operator: CriteriaOperatorType.AND,
      criteria: [
        {
          fieldCriterion: {
            field: UserCriteriaFieldType.CUSTOM_STATUS_TYPE,
            type: CriteriaType.EQ,
            values: [PatientStatusType.FINAL_CONTACT],
          },
        },
        {
          fieldCriterion: {
            field: UserCriteriaFieldType.CUSTOM_STATUS_VALUE,
            type: CriteriaType.NOT_EQ,
            values: [PatientFinalContactStatusType.CONTACT_COMPLETED],
          },
        },
      ],
    },
    {
      fieldCriterion: {
        field: UserCriteriaFieldType.CUSTOM_STATUS_TYPE,
        type: CriteriaType.NOT_EQ,
        values: [PatientStatusType.FINAL_CONTACT],
      },
    },
  ],
};

const toCloseoutVisitCriterionV1 = (closeout?: CloseoutVisitCriteria): Maybe<CriterionV1> => {
  if (!closeout || closeout.statuses.length === 0) {
    return undefined;
  }

  const isBooked = closeout.statuses.includes(BookedVisitStatusType.BOOKED);
  const isNotBooked = closeout.statuses.includes('NOT_BOOKED');
  const isPassed = closeout.statuses.includes(BookedVisitStatusType.PASSED);
  const isCloseoutCompleted = closeout.statuses.includes(BookedVisitStatusType.CLOSE_OUT_COMPLETED);
  const isCloseoutOverdue = closeout.statuses.includes(BookedVisitStatusType.CLOSE_OUT_OVERDUE);

  const criteria: CriterionV1[] = [];

  if (isNotBooked) {
    criteria.push({
      operator: CriteriaOperatorType.AND,
      criteria: [createCloseoutValueCriterion(CriteriaType.EXISTS, [false], 'unixMillis')],
    });
  }

  if (isCloseoutCompleted) {
    criteria.push({
      operator: CriteriaOperatorType.AND,
      criteria: [
        {
          operator: CriteriaOperatorType.AND,
          criteria: [
            {
              fieldCriterion: {
                field: UserCriteriaFieldType.CUSTOM_STATUS_TYPE,
                type: CriteriaType.EQ,
                values: [PatientStatusType.FINAL_CONTACT],
              },
            },
            {
              fieldCriterion: {
                field: UserCriteriaFieldType.CUSTOM_STATUS_VALUE,
                type: CriteriaType.EQ,
                values: [PatientFinalContactStatusType.CONTACT_COMPLETED],
              },
            },
          ],
        },
        {
          operator: CriteriaOperatorType.AND,
          criteria: [visitMetaCriterion, createCloseoutValueCriterion(CriteriaType.EXISTS, [true])],
        },
      ],
    });
  }

  if (isCloseoutOverdue) {
    criteria.push({
      operator: CriteriaOperatorType.AND,
      criteria: [
        noFinalContactCriterion,
        {
          operator: CriteriaOperatorType.AND,
          criteria: [
            visitMetaCriterion,
            createCloseoutValueCriterion(
              CriteriaType.LESS_THAN,
              [Date.now() - BookedVisit.closeoutOverduePeriodMillis],
              'unixMillis',
            ),
          ],
        },
      ],
    });
  }

  if (isBooked) {
    criteria.push({
      operator: CriteriaOperatorType.AND,
      criteria: [
        noFinalContactCriterion,
        {
          operator: CriteriaOperatorType.AND,
          criteria: [
            visitMetaCriterion,
            createCloseoutValueCriterion(
              CriteriaType.GREATER_THAN,
              [Date.now() - PatientVisitUtil.visitPassedPeriodMillis],
              'unixMillis',
            ),
          ],
        },
      ],
    });
  }

  if (isPassed) {
    criteria.push({
      operator: CriteriaOperatorType.AND,
      criteria: [
        noFinalContactCriterion,
        {
          operator: CriteriaOperatorType.AND,
          criteria: [
            visitMetaCriterion,
            createCloseoutValueCriterion(
              CriteriaType.LESS_THAN,
              [Date.now() - PatientVisitUtil.visitPassedPeriodMillis],
              'unixMillis',
            ),
            createCloseoutValueCriterion(
              CriteriaType.GREATER_THAN,
              [Date.now() - BookedVisit.closeoutOverduePeriodMillis],
              'unixMillis',
            ),
          ],
        },
      ],
    });
  }

  return {
    operator: CriteriaOperatorType.OR,
    criteria,
  };
};

const patientRoleCriterionV1 = (): CriterionV1 => ({
  fieldCriterion: {
    field: UserCriteriaFieldType.ROLE,
    type: CriteriaType.EQ,
    values: [RoleType.PATIENT],
  },
});

const isNonEmptyArray = <T>(maybeArray: undefined | T[]): maybeArray is T[] =>
  Array.isArray(maybeArray) && maybeArray.length > 0;

const toComplianceCriterionV1 = (compliance?: ComplianceCriteria): Maybe<CriterionV1> => {
  const totalComplianceStatuses = compliance?.statuses;
  const hasTotalComplianceStatuses = isNonEmptyArray(totalComplianceStatuses);
  const recentComplianceStatuses = compliance?.recentStatuses;
  const hasRecentComplianceStatuses = isNonEmptyArray(recentComplianceStatuses);

  if (!hasTotalComplianceStatuses && !hasRecentComplianceStatuses) {
    return undefined;
  }

  const criteria: CriterionV1[] = [
    {
      fieldCriterion: {
        field: UserCriteriaFieldType.META_TYPE,
        type: CriteriaType.EQ,
        values: ['PATIENT_COMPLIANCE_V1'],
      },
    },
  ];

  if (hasTotalComplianceStatuses) {
    criteria.push({
      fieldCriterion: {
        field: UserCriteriaFieldType.META_VALUE,
        subField: 'state',
        type: CriteriaType.IN,
        values: totalComplianceStatuses,
      },
    });
  }

  if (hasRecentComplianceStatuses) {
    criteria.push({
      fieldCriterion: {
        field: UserCriteriaFieldType.META_VALUE,
        subField: 'recentState',
        type: CriteriaType.IN,
        values: recentComplianceStatuses,
      },
    });
  }

  return {
    operator: CriteriaOperatorType.AND,
    criteria,
  };
};

const toPatientStatusCriterionV1 = (statusCriteria: Maybe<PatientStatusCriteria>): Maybe<CriterionV1> => {
  const criteria: CriterionV1[] = [
    toStatusCriteria(PatientStatusType.STUDY, statusCriteria?.studyStatuses),
    toStatusCriteria(PatientStatusType.MEDICATION, calculateStatuses(statusCriteria?.medicationStatuses)),
    toStatusCriteria(PatientStatusType.APP, statusCriteria?.appStatuses),
    toStatusCriteria(PatientStatusType.FINAL_CONTACT, statusCriteria?.finalContactStatuses),
    toStatusCriteria(PatientStatusType.VITAL, statusCriteria?.vitalStatuses),
    toLastClientActivityStatusCriteria(statusCriteria?.inactivityThresholdInDays),
  ].filter(isDefined);

  return criteria.length
    ? {
        operator: CriteriaOperatorType.AND,
        criteria,
      }
    : undefined;
};

const from = ({
  criteria: { org, statuses, compliance, freeText, closeout, userIds, tasksPaused, userStatuses },
  fields,
  sorting,
}: PagedPatientQueryIn): PatientQuery => {
  const rootCriterion: CriterionV1 = {
    operator: CriteriaOperatorType.AND,
    criteria: [],
  };

  const appendCriterion = (criterionV1: Maybe<CriterionV1>): void => {
    if (criterionV1) {
      rootCriterion.criteria?.push(criterionV1);
    }
  };

  appendCriterion(toFreeTextCriterionV1(freeText));
  appendCriterion(toOrgCriterionV1(org));
  appendCriterion(toPatientStatusCriterionV1(statuses));
  appendCriterion(toComplianceCriterionV1(compliance));
  appendCriterion(toCloseoutVisitCriterionV1(closeout));
  appendCriterion(toPausedTasksCriterionV1(tasksPaused));
  appendCriterion(patientRoleCriterionV1());
  appendCriterion(toUserIdsCriterionV1(userIds));
  appendCriterion(toUserStatusCriterionV1(userStatuses));

  const queryV1: QueryV1 = {
    fields,
    criterion: rootCriterion,
    sort: sorting,
  };

  return {
    queryV1,
  };
};

const DEFAULT_CRITERIA: Readonly<PatientCriteria> = {
  org: { siteIds: [] },
  compliance: { statuses: undefined },
  freeText: { text: '' },
  tasksPaused: false,
  statuses: { studyStatuses: [], medicationStatuses: [], appStatuses: [] },
  closeout: { statuses: [] },
};

export const PatientQueryMapper = {
  from,
  defaultFields: DEFAULT_FIELDS,
  defaultSorting: DEFAULT_SORTING,
  defaultCriteria: DEFAULT_CRITERIA,
  toPatientStatusCriterionV1,
};
